import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { usePlatform } from '@/lib/platform/context';
import type { Autostart } from '@/lib/platform/types';

const AUTOSTART_KEY = ['platform', 'autostart'] as const;

function useAutostart(autostart: Autostart | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: AUTOSTART_KEY,
    queryFn: () => (autostart ? autostart.get() : Promise.reject(new Error('no autostart'))),
    enabled: autostart !== undefined,
  });
  const answer = useMutation({
    mutationFn: (enabled: boolean) =>
      autostart ? autostart.set(enabled) : Promise.reject(new Error('no autostart')),
    onSettled: () => queryClient.invalidateQueries({ queryKey: AUTOSTART_KEY }),
  });
  return { query, answer };
}

/**
 * PRD VS-055: "optional launch-at-login, asked once on first run, never
 * assumed". Shown on desktop until answered either way; the answer is
 * recorded by the Rust shell (TechSpec §10.2), so it never comes back.
 */
export function AutostartPrompt() {
  const { t } = useTranslation('settings');
  const { autostart } = usePlatform();
  const { query, answer } = useAutostart(autostart);
  const titleId = useId();

  if (!autostart || query.data?.asked !== false) return null;

  return (
    <Card role="region" aria-labelledby={titleId} className="mb-6 max-w-2xl">
      <CardHeader className="pb-3">
        <h2 id={titleId} className="font-semibold">
          {t('autostart.prompt.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('autostart.prompt.description')}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {answer.isError && (
          <Alert variant="destructive" role="alert">
            {t('autostart.error')}
          </Alert>
        )}
        <div className="flex flex-wrap gap-3">
          <Button size="sm" onClick={() => answer.mutate(true)} disabled={answer.isPending}>
            {answer.isPending && answer.variables && <Spinner />}
            {t('autostart.prompt.accept')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => answer.mutate(false)}
            disabled={answer.isPending}
          >
            {t('autostart.prompt.decline')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** The same choice, changeable at any time from the settings page. */
export function AutostartSetting() {
  const { t } = useTranslation('settings');
  const { autostart } = usePlatform();
  const { query, answer } = useAutostart(autostart);
  const inputId = useId();
  const hintId = useId();

  if (!autostart) return null;
  if (query.isPending) {
    return (
      <output aria-live="polite" className="flex items-center gap-2 text-muted-foreground">
        <Spinner />
        {t('autostart.loading')}
      </output>
    );
  }
  if (query.isError) {
    return (
      <Alert variant="destructive" role="alert">
        {t('autostart.error')}
      </Alert>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <input
          id={inputId}
          type="checkbox"
          className="h-4 w-4 accent-primary"
          checked={answer.isPending ? answer.variables : query.data.enabled}
          disabled={answer.isPending}
          aria-describedby={hintId}
          onChange={(event) => answer.mutate(event.target.checked)}
        />
        <label htmlFor={inputId} className="text-sm">
          {t('autostart.label')}
        </label>
      </div>
      <p id={hintId} className="text-sm text-muted-foreground">
        {t('autostart.hint')}
      </p>
      {answer.isError && (
        <Alert variant="destructive" role="alert">
          {t('autostart.error')}
        </Alert>
      )}
    </div>
  );
}
